import { ACTION_IDS, type ActionId } from "./actions.ts";

export type Platform = "mac" | "other";

export interface KeyChord {
  type: "key";
  code: string;
  alt?: boolean;
  shift?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  /** Ctrl on other platforms, Command on Mac. */
  mod?: boolean;
}

export interface PointerChord {
  type: "drag" | "click";
  button: number;
  alt?: boolean;
  shift?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  /** Ctrl on other platforms, Command on Mac. */
  mod?: boolean;
}

export interface WheelChord {
  type: "wheel";
  alt?: boolean;
  shift?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  /** Ctrl on other platforms, Command on Mac. */
  mod?: boolean;
}

export type Binding = KeyChord | PointerChord | WheelChord;

export type Keymap = Record<ActionId, Binding[]>;

export interface KeyState {
  code: string;
  alt: boolean;
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
}

export interface PointerState {
  kind: "drag" | "click";
  button: number;
  alt: boolean;
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
}

export interface PrefStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const PRESET_IDS = ["default"] as const;
export type PresetId = (typeof PRESET_IDS)[number];

const STORAGE_KEY = "mapus.keymap";

/** Shipped presets. A future Blender preset is another object in this map. */
export const presets: Record<PresetId, Keymap> = {
  default: {
    panView: [{ type: "drag", button: 0, alt: true }],
    rotateView: [{ type: "drag", button: 0, alt: true, shift: true }],
    lassoSelect: [{ type: "drag", button: 0 }],
    lassoAdd: [{ type: "drag", button: 0, shift: true }],
    lassoSubtract: [{ type: "drag", button: 0, mod: true }],
    drawWorkArea: [{ type: "drag", button: 0, shift: true, mod: true }],
    undo: [{ type: "key", code: "KeyZ", mod: true }],
    redo: [
      { type: "key", code: "KeyY", mod: true },
      { type: "key", code: "KeyZ", mod: true, shift: true },
    ],
    placeAtCenter: [{ type: "click", button: 2 }],
    deselect: [{ type: "key", code: "Escape" }],
    selectAll: [{ type: "key", code: "KeyA" }],
    deselectAll: [{ type: "key", code: "KeyA", alt: true }],
    cycleSelectTool: [{ type: "key", code: "KeyW" }],
    moveSelected: [{ type: "key", code: "KeyG" }],
    join: [{ type: "key", code: "KeyJ" }],
    merge: [{ type: "key", code: "KeyM" }],
    copyStyle: [{ type: "key", code: "KeyC", mod: true }],
    pasteStyle: [{ type: "key", code: "KeyV", mod: true }],
    toggleDecorative: [],
    savePiece: [{ type: "key", code: "KeyS", mod: true }],
    exitEdit: [{ type: "key", code: "KeyX", mod: true }],
    enterEdit: [{ type: "key", code: "KeyE", mod: true }],
    fillTool: [{ type: "key", code: "KeyF" }],
    clearRegion: [{ type: "click", button: 0, alt: true }],
    exitFill: [
      { type: "key", code: "Escape" },
      { type: "click", button: 2 },
    ],
    deleteSelected: [{ type: "key", code: "Delete" }],
    deleteMenu: [{ type: "key", code: "KeyX" }],
    deletePointAtCursor: [{ type: "click", button: 0, alt: true }],
    erase: [{ type: "key", code: "KeyE" }],
    togglePoints: [{ type: "key", code: "KeyH" }],
    toggleSnap: [{ type: "key", code: "KeyS" }],
    fillRoadsTool: [{ type: "key", code: "KeyR" }],
    cycleRoadsTool: [{ type: "key", code: "KeyW" }],
    roadsClear: [{ type: "key", code: "KeyC" }],
    roadsFill: [{ type: "key", code: "KeyF" }],
    roadsBoxFill: [{ type: "drag", button: 0 }],
    roadsCircleStroke: [{ type: "drag", button: 0 }],
    workArea: [{ type: "key", code: "KeyB" }],
    clearWorkArea: [{ type: "key", code: "KeyB", shift: true }],
    northUp: [{ type: "key", code: "KeyN" }],
    // Opens or closes the left sheet and the right detail panel together.
    toggleDrawers: [{ type: "key", code: "Tab" }],
    toggleBasemap: [{ type: "key", code: "KeyV" }],
    toggleStreetView: [{ type: "key", code: "KeyP" }],
    moveCanvas: [{ type: "drag", button: 0 }],
    resizeCanvas: [{ type: "drag", button: 0 }],
    rotateCanvas: [{ type: "drag", button: 0 }],
    select: [{ type: "click", button: 0 }],
    toggleSelect: [{ type: "click", button: 0, shift: true }],
    zoomMap: [{ type: "wheel" }],
    // mod is Ctrl on other platforms and Command on Mac. ctrl also covers
    // trackpad pinch, which browsers report as ctrlKey on every platform.
    resizeEraseBrush: [
      { type: "wheel", mod: true },
      { type: "wheel", ctrl: true },
    ],
    // Same chords as the erase brush. Erase mode resizes the brush; Circle select resizes the circle.
    resizeSelectCircle: [
      { type: "wheel", mod: true },
      { type: "wheel", ctrl: true },
    ],
    // Same chords. Fill Roads Circle resizes its own brush; other modes still zoom.
    resizeRoadsBrush: [
      { type: "wheel", mod: true },
      { type: "wheel", ctrl: true },
    ],
    zoomIn: [
      { type: "key", code: "Equal" },
      { type: "key", code: "NumpadAdd" },
    ],
    zoomOut: [
      { type: "key", code: "Minus" },
      { type: "key", code: "NumpadSubtract" },
    ],
    zoomInMore: [
      { type: "key", code: "Equal", shift: true },
      { type: "key", code: "NumpadAdd", shift: true },
    ],
    zoomOutMore: [
      { type: "key", code: "Minus", shift: true },
      { type: "key", code: "NumpadSubtract", shift: true },
    ],
    panLeft: [{ type: "key", code: "ArrowLeft" }],
    panRight: [{ type: "key", code: "ArrowRight" }],
    panUp: [{ type: "key", code: "ArrowUp" }],
    panDown: [{ type: "key", code: "ArrowDown" }],
    movePoint: [{ type: "drag", button: 0 }],
    eraseStroke: [{ type: "drag", button: 0 }],
    moveBoundary: [{ type: "drag", button: 0 }],
    confirmGrab: [
      { type: "key", code: "Enter" },
      { type: "key", code: "NumpadEnter" },
      { type: "click", button: 0 },
    ],
    cancelGrab: [
      { type: "key", code: "Escape" },
      { type: "click", button: 2 },
    ],
    confirmDeleteMenu: [
      { type: "key", code: "Enter" },
      { type: "key", code: "NumpadEnter" },
    ],
    cancelDeleteMenu: [
      { type: "key", code: "Escape" },
      { type: "click", button: 2 },
    ],
    deleteMenuNext: [
      { type: "key", code: "ArrowDown" },
      { type: "key", code: "ArrowRight" },
    ],
    deleteMenuPrev: [
      { type: "key", code: "ArrowUp" },
      { type: "key", code: "ArrowLeft" },
    ],
    // Menu accelerators. They match only while the delete menu is open.
    deleteMenuPoints: [{ type: "key", code: "KeyD" }],
    deleteMenuLines: [{ type: "key", code: "KeyL" }],
    deleteMenuPointsAndLines: [{ type: "key", code: "KeyK" }],
  },
};

export function detectPlatform(nav?: { platform?: string; userAgent?: string }): Platform {
  const blob = `${nav?.platform ?? ""} ${nav?.userAgent ?? ""}`;
  return /Mac|iPhone|iPad|iPod/.test(blob) ? "mac" : "other";
}

/** Undo chord as written in toasts. */
export function undoHint(platform: Platform): string {
  return platform === "mac" ? "⌘Z" : "Ctrl+Z";
}

export function createKeymap(preset: PresetId = "default"): Keymap {
  return structuredClone(presets[preset]);
}

export function matchKey(keymap: Keymap, state: KeyState, platform: Platform): ActionId | null {
  return firstMatch(keymap, (binding) => binding.type === "key" && keyMatches(binding, state, platform));
}

export function matchPointer(keymap: Keymap, state: PointerState, platform: Platform): ActionId | null {
  return firstMatch(
    keymap,
    (binding) =>
      (binding.type === "drag" || binding.type === "click") && pointerMatches(binding, state, platform),
  );
}

export function matchWheel(keymap: Keymap, state: ModifierState, platform: Platform): ActionId | null {
  return firstMatch(keymap, (binding) => binding.type === "wheel" && modifierStateMatches(binding, state, platform));
}

export function actionMatchesKey(keymap: Keymap, action: ActionId, state: KeyState, platform: Platform): boolean {
  return keymap[action].some((binding) => binding.type === "key" && keyMatches(binding, state, platform));
}

export function actionMatchesWheel(
  keymap: Keymap,
  action: ActionId,
  state: ModifierState,
  platform: Platform,
): boolean {
  return keymap[action].some((binding) => binding.type === "wheel" && modifierStateMatches(binding, state, platform));
}

export function actionMatchesPointer(
  keymap: Keymap,
  action: ActionId,
  state: PointerState,
  platform: Platform,
): boolean {
  return keymap[action].some(
    (binding) =>
      (binding.type === "drag" || binding.type === "click") && pointerMatches(binding, state, platform),
  );
}

/** True when any pointer binding of this action uses the button, ignoring modifiers. */
export function actionUsesButton(keymap: Keymap, action: ActionId, button: number): boolean {
  return keymap[action].some(
    (binding) => (binding.type === "drag" || binding.type === "click") && binding.button === button,
  );
}

/** Alt-drag (and any other view-gesture binding) must not select or edit. */
export function viewGesture(keymap: Keymap, state: PointerState, platform: Platform): "panView" | "rotateView" | null {
  const action = matchPointer(keymap, { ...state, kind: "drag" }, platform);
  return action === "panView" || action === "rotateView" ? action : null;
}

export function blocksEditing(keymap: Keymap, state: PointerState, platform: Platform): boolean {
  return viewGesture(keymap, state, platform) != null;
}

export interface ModifierState {
  alt: boolean;
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
}

/**
 * View chord armed by modifiers alone, before the drag starts.
 * A binding with no modifier is ignored, so an unmodified drag does not
 * turn the resting cursor into a hand. Rotate wins when both could match.
 */
export function heldView(
  keymap: Keymap,
  mods: ModifierState,
  platform: Platform,
): "panView" | "rotateView" | null {
  for (const action of ["rotateView", "panView"] as const) {
    for (const binding of keymap[action]) {
      if (binding.type !== "drag" || !chordHasModifier(binding)) continue;
      if (modifierStateMatches(binding, mods, platform)) return action;
    }
  }
  return null;
}

export function findConflict(
  keymap: Keymap,
  action: ActionId,
  binding: Binding,
  platform: Platform,
): ActionId | null {
  const signature = bindingSignature(binding, platform);
  for (const id of ACTION_IDS) {
    if (id === action || shareChord(action, id)) continue;
    for (const other of keymap[id]) {
      if (bindingSignature(other, platform) === signature) return id;
    }
  }
  return null;
}

export function conflicts(keymap: Keymap, platform: Platform): Array<{ a: ActionId; b: ActionId }> {
  const seen = new Map<string, ActionId>();
  const found: Array<{ a: ActionId; b: ActionId }> = [];
  for (const id of ACTION_IDS) {
    for (const binding of keymap[id]) {
      const signature = bindingSignature(binding, platform);
      const previous = seen.get(signature);
      if (previous && previous !== id && !shareChord(previous, id)) found.push({ a: previous, b: id });
      else if (!previous) seen.set(signature, id);
    }
  }
  return found;
}

export function replaceBinding(keymap: Keymap, action: ActionId, index: number, binding: Binding): Keymap {
  const next = structuredClone(keymap);
  const list = next[action].slice();
  if (index < 0 || index >= list.length) return next;
  list[index] = binding;
  next[action] = list;
  return next;
}

export interface StoredKeymap {
  preset: PresetId;
  keymap: Keymap;
}

export function loadKeymap(store: PrefStore): StoredKeymap {
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return { preset: "default", keymap: createKeymap() };
    const parsed = JSON.parse(raw) as { preset?: unknown; keymap?: unknown };
    const preset = isPresetId(parsed.preset) ? parsed.preset : "default";
    return { preset, keymap: sanitizeKeymap(parsed.keymap, preset) };
  } catch {
    return { preset: "default", keymap: createKeymap() };
  }
}

export function saveKeymap(store: PrefStore, stored: StoredKeymap): void {
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* private mode */
  }
}

export function resetKeymap(store: PrefStore, preset: PresetId = "default"): StoredKeymap {
  const stored = { preset, keymap: createKeymap(preset) };
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  return stored;
}

const MODIFIER_CODES = new Set([
  "AltLeft",
  "AltRight",
  "ShiftLeft",
  "ShiftRight",
  "ControlLeft",
  "ControlRight",
  "MetaLeft",
  "MetaRight",
]);

export function bindingFromKey(state: KeyState, platform: Platform): KeyChord | null {
  if (MODIFIER_CODES.has(state.code)) return null;
  const binding: KeyChord = { type: "key", code: state.code };
  if (state.alt) binding.alt = true;
  if (state.shift) binding.shift = true;
  const modDown = platform === "mac" ? state.meta : state.ctrl;
  const otherDown = platform === "mac" ? state.ctrl : state.meta;
  if (modDown && !otherDown) binding.mod = true;
  else {
    if (state.ctrl) binding.ctrl = true;
    if (state.meta) binding.meta = true;
  }
  return binding;
}

export function bindingFromPointer(state: PointerState, platform: Platform): PointerChord {
  const binding: PointerChord = { type: state.kind, button: state.button };
  applyModifiers(binding, state, platform);
  return binding;
}

export function bindingFromWheel(state: ModifierState, platform: Platform): WheelChord {
  const binding: WheelChord = { type: "wheel" };
  applyModifiers(binding, state, platform);
  return binding;
}

/** Stable pieces for the settings list. `mod` is resolved to ctrl or meta. */
export function bindingTokens(binding: Binding, platform: Platform): string[] {
  const tokens: string[] = [];
  if (binding.mod) tokens.push(platform === "mac" ? "meta" : "ctrl");
  if (binding.ctrl) tokens.push("ctrl");
  if (binding.alt) tokens.push("alt");
  if (binding.shift) tokens.push("shift");
  if (binding.meta) tokens.push("meta");
  if (binding.type === "drag" || binding.type === "click") {
    if (binding.button === 1) tokens.push("middle");
    if (binding.button === 2) tokens.push("right");
  }
  if (binding.type === "key") tokens.push(formatKeyCode(binding.code));
  else if (binding.type === "wheel") tokens.push("wheel");
  else if (binding.type === "drag") tokens.push("drag");
  else tokens.push("click");
  return tokens;
}

export function formatKeyCode(code: string): string {
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code === "Escape") return "Esc";
  if (code === "Space") return "Space";
  if (code === "Backspace") return "Backspace";
  if (code === "Delete") return "Delete";
  if (code === "Tab") return "Tab";
  return code;
}

/**
 * Chords that only apply while another gesture is already active, or that
 * win by what is under the cursor. They stay out of the global first match
 * so Escape still deselects and a plain drag still lassos.
 */
const CONTEXT_ACTIONS = new Set<ActionId>([
  "movePoint",
  "eraseStroke",
  "moveBoundary",
  "moveCanvas",
  "resizeCanvas",
  "rotateCanvas",
  "confirmGrab",
  "cancelGrab",
  "confirmDeleteMenu",
  "cancelDeleteMenu",
  "deleteMenuNext",
  "deleteMenuPrev",
  "deleteMenuPoints",
  "deleteMenuLines",
  "deleteMenuPointsAndLines",
  "clearRegion",
  "exitFill",
  "cycleRoadsTool",
  "roadsClear",
  "roadsFill",
  "roadsBoxFill",
  "roadsCircleStroke",
]);

/**
 * These pairs use one chord in different contexts (a mode, or a hit target).
 * Sharing it is the default, so it is not reported as a conflict.
 */
const SHARED_CHORDS: ReadonlyArray<readonly ActionId[]> = [
  ["deselect", "cancelGrab", "cancelDeleteMenu"],
  ["placeAtCenter", "cancelGrab", "cancelDeleteMenu"],
  ["select", "confirmGrab"],
  ["confirmGrab", "confirmDeleteMenu"],
  [
    "lassoSelect",
    "movePoint",
    "eraseStroke",
    "moveBoundary",
    "moveCanvas",
    "resizeCanvas",
    "rotateCanvas",
    "roadsBoxFill",
    "roadsCircleStroke",
  ],
  ["resizeEraseBrush", "resizeSelectCircle", "resizeRoadsBrush"],
  ["panLeft", "deleteMenuPrev"],
  ["panRight", "deleteMenuNext"],
  ["panUp", "deleteMenuPrev"],
  ["panDown", "deleteMenuNext"],
];

/** Keys and clicks that run only while Fill Region is active. */
const FILL_MODE_ACTIONS = new Set<ActionId>(["clearRegion", "exitFill"]);

/** Keys that run only while the Fill Roads tool is open. */
const ROADS_MODE_ACTIONS = new Set<ActionId>(["cycleRoadsTool", "roadsClear", "roadsFill"]);

/** Keys that run only while the delete menu is open. */

const DELETE_MENU_ACTIONS = new Set<ActionId>([
  "confirmDeleteMenu",
  "cancelDeleteMenu",
  "deleteMenuNext",
  "deleteMenuPrev",
  "deleteMenuPoints",
  "deleteMenuLines",
  "deleteMenuPointsAndLines",
]);

function shareChord(a: ActionId, b: ActionId): boolean {
  if (SHARED_CHORDS.some((group) => group.includes(a) && group.includes(b))) return true;
  // A menu accelerator and a global binding can use the same key. Only one of them is live.
  const aMenu = DELETE_MENU_ACTIONS.has(a);
  const bMenu = DELETE_MENU_ACTIONS.has(b);
  if (aMenu !== bMenu) return true;
  if (ROADS_MODE_ACTIONS.has(a) !== ROADS_MODE_ACTIONS.has(b)) return true;
  const aFill = FILL_MODE_ACTIONS.has(a);
  const bFill = FILL_MODE_ACTIONS.has(b);
  return aFill !== bFill;
}

/** Bare letter or digit for a menu underline. Modified chords stay in the key hint only. */
export function acceleratorLetter(keymap: Keymap, action: ActionId): string | null {
  const binding = keymap[action].find((item): item is KeyChord => item.type === "key");
  if (!binding) return null;
  if (binding.alt || binding.shift || binding.ctrl || binding.meta || binding.mod) return null;
  if (binding.code.startsWith("Key") && binding.code.length === 4) return binding.code.slice(3);
  if (binding.code.startsWith("Digit") && binding.code.length === 6) return binding.code.slice(5);
  return null;
}

/** Index of the accelerator in a label. A word-start match wins, so L marks Lines rather than Delete. */
export function acceleratorIndex(label: string, letter: string | null): number {
  if (!letter) return -1;
  const lower = label.toLowerCase();
  const needle = letter.toLowerCase();
  for (let i = 0; i < lower.length; i++) {
    const wordStart = i === 0 || /\s/.test(lower[i - 1] ?? "");
    if (wordStart && lower.startsWith(needle, i)) return i;
  }
  return lower.indexOf(needle);
}

function firstMatch(keymap: Keymap, test: (binding: Binding) => boolean): ActionId | null {
  for (const id of ACTION_IDS) {
    if (CONTEXT_ACTIONS.has(id)) continue;
    if (keymap[id].some(test)) return id;
  }
  return null;
}

function applyModifiers(
  binding: { alt?: boolean; shift?: boolean; ctrl?: boolean; meta?: boolean; mod?: boolean },
  state: ModifierState,
  platform: Platform,
): void {
  if (state.alt) binding.alt = true;
  if (state.shift) binding.shift = true;
  const modDown = platform === "mac" ? state.meta : state.ctrl;
  const otherDown = platform === "mac" ? state.ctrl : state.meta;
  if (modDown && !otherDown) binding.mod = true;
  else {
    if (state.ctrl) binding.ctrl = true;
    if (state.meta) binding.meta = true;
  }
}

function keyMatches(binding: KeyChord, state: KeyState, platform: Platform): boolean {
  if (binding.code !== state.code) return false;
  return modifierStateMatches(binding, state, platform);
}

function pointerMatches(binding: PointerChord, state: PointerState, platform: Platform): boolean {
  if (binding.type !== state.kind || binding.button !== state.button) return false;
  return modifierStateMatches(binding, state, platform);
}

function chordHasModifier(binding: { alt?: boolean; shift?: boolean; ctrl?: boolean; meta?: boolean; mod?: boolean }): boolean {
  return !!(binding.alt || binding.shift || binding.ctrl || binding.meta || binding.mod);
}

function modifierStateMatches(
  binding: { alt?: boolean; shift?: boolean; ctrl?: boolean; meta?: boolean; mod?: boolean },
  state: ModifierState,
  platform: Platform,
): boolean {
  if (!!binding.alt !== state.alt) return false;
  if (!!binding.shift !== state.shift) return false;
  const modDown = platform === "mac" ? state.meta : state.ctrl;
  const otherDown = platform === "mac" ? state.ctrl : state.meta;
  if (binding.mod) return modDown && !otherDown;
  if (!!binding.ctrl !== state.ctrl) return false;
  if (!!binding.meta !== state.meta) return false;
  return true;
}

function bindingSignature(binding: Binding, platform: Platform): string {
  return bindingTokens(binding, platform).join("+");
}

function isPresetId(value: unknown): value is PresetId {
  return value === "default";
}

function sanitizeKeymap(value: unknown, preset: PresetId): Keymap {
  const base = createKeymap(preset);
  if (!value || typeof value !== "object") return base;
  const raw = value as Record<string, unknown>;
  for (const id of ACTION_IDS) {
    const list = raw[id];
    if (!Array.isArray(list)) continue;
    const bindings = list.map(sanitizeBinding).filter((binding): binding is Binding => binding != null);
    if (bindings.length > 0) base[id] = bindings;
  }
  // Tab now opens and closes the side drawers, and Show/Hide Points is back on H.
  // A keymap saved before the drawer toggle existed still carries the old Tab
  // default for points, so move it to H. Custom bindings stay as they are.
  if (!Object.prototype.hasOwnProperty.call(raw, "toggleDrawers")) {
    const tp = base.togglePoints;
    const onlyTab =
      tp.length === 1 &&
      tp[0]?.type === "key" &&
      tp[0].code === "Tab" &&
      !chordHasModifier(tp[0]);
    if (onlyTab) base.togglePoints = [{ type: "key", code: "KeyH" }];
  }
  // Clear Canvas used to share plain N with North and wiped the canvas without
  // asking. While North still holds N, move that old default to Shift+B; other
  // chords the user chose stay.
  const plainN = (binding: Binding) => binding.type === "key" && binding.code === "KeyN" && !chordHasModifier(binding);
  if (base.northUp.some(plainN) && base.clearWorkArea.some(plainN)) {
    const shiftB: KeyChord = { type: "key", code: "KeyB", shift: true };
    const kept = base.clearWorkArea.filter((binding) => !plainN(binding));
    const hasShiftB = kept.some((binding) => bindingSignature(binding, "other") === bindingSignature(shiftB, "other"));
    base.clearWorkArea = hasShiftB ? kept : [...kept, shiftB];
  }
  return base;
}

function sanitizeBinding(value: unknown): Binding | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const flag = (name: string) => (raw[name] === true ? true : undefined);
  if (raw.type === "key" && typeof raw.code === "string" && raw.code.length > 0) {
    const binding: KeyChord = { type: "key", code: raw.code };
    if (flag("alt")) binding.alt = true;
    if (flag("shift")) binding.shift = true;
    if (flag("ctrl")) binding.ctrl = true;
    if (flag("meta")) binding.meta = true;
    if (flag("mod")) binding.mod = true;
    return binding;
  }
  if ((raw.type === "drag" || raw.type === "click") && typeof raw.button === "number") {
    const binding: PointerChord = { type: raw.type, button: raw.button };
    if (flag("alt")) binding.alt = true;
    if (flag("shift")) binding.shift = true;
    if (flag("ctrl")) binding.ctrl = true;
    if (flag("meta")) binding.meta = true;
    if (flag("mod")) binding.mod = true;
    return binding;
  }
  if (raw.type === "wheel") {
    const binding: WheelChord = { type: "wheel" };
    if (flag("alt")) binding.alt = true;
    if (flag("shift")) binding.shift = true;
    if (flag("ctrl")) binding.ctrl = true;
    if (flag("meta")) binding.meta = true;
    if (flag("mod")) binding.mod = true;
    return binding;
  }
  return null;
}
